<script>
import { useRequestIdleCallback } from '@common/utils/web-apis';
import { FdxDropdown } from '@foodics/ui-common';
import { useNotificationsPanel } from '@mms/composables/useNotificationsPanel';
import { useUserStore } from '@mms/composables/useUserStore';
import mixpanel from 'mixpanel-browser';
import { defineAsyncComponent } from 'vue';

export default {
  components: {
    FdxDropdown,
    NotificationsPanel: defineAsyncComponent(() => import('./NotificationsPanel.vue')),
  },
  methods: {
    logout() {
      mixpanel.reset();
      this.$router.replace('/logout');
    },
  },
  mounted() {
    const { executeCallback } = useRequestIdleCallback(
      () => {
        this.getUnreadNotificationsCount();
      },
      { pauseTime: 2 * 60 * 1000, repeat: true }
    );
    executeCallback();
  },
  setup() {
    const { getUnreadNotificationsCount, unreadNotificationsCount } = useNotificationsPanel();

    const { user } = useUserStore();

    return {
      getUnreadNotificationsCount,
      unreadNotificationsCount,
      user,
    };
  },
};
</script>

<template>
  <header id="header" data-testid="header" class="relative shrink-0 bg-white">
    <div
      class="flex h-16 w-full items-center justify-between border-b border-gray-300 px-6 lg:justify-end lg:px-10 2xl:px-16"
    >
      <div class="block lg:hidden">
        <router-link to="/">
          <img src="@assets/images/logo-foodics.png" width="200px" alt="logo" />
        </router-link>
      </div>
      <div class="flex items-center gap-3">
        <fdx-dropdown :popper-hide-triggers="[]" placement="bottom-end">
          <template #trigger>
            <div
              class="border-gray-70 relative flex h-11 w-11 items-center justify-center rounded-full border"
            >
              <span
                v-if="unreadNotificationsCount > 0"
                class="pointer-events-none absolute -end-1 -top-1 animate-bounce rounded-full bg-red-500 px-1.5 py-0.5 text-xs text-white"
              >
                {{ unreadNotificationsCount > 9 ? '9+' : unreadNotificationsCount }}
              </span>
              <button class="flex h-full w-full items-center justify-center">
                <fdx-icon-notifications />
              </button>
            </div>
          </template>
          <template #menu>
            <notifications-panel />
          </template>
        </fdx-dropdown>
        <fdx-dropdown placement="bottom-end">
          <template #trigger>
            <button
              class="border-gray-70 flex h-11 w-11 items-center justify-center rounded-full border"
              data-testid="user-profile-button"
            >
              <fdx-icon-user-header />
            </button>
          </template>
          <template #menu>
            <div class="py-2">
              <div class="flex w-full px-4 py-2">
                <div
                  class="border-gray-70 me-2 flex h-11 w-11 items-center justify-center rounded-full border"
                >
                  <fdx-icon-user-header />
                </div>
                <div>
                  {{ user.name }} <br />
                  <span class="font-thin">
                    {{ user.email }}
                  </span>
                </div>
              </div>
              <hr />

              <button
                class="block w-full px-4 py-2 text-start hover:text-primary"
                @click="logout"
                data-testid="logout-button"
              >
                <fdx-icon-logout class="me-2 inline size-4" />
                {{ $t('logout') }}
              </button>
            </div>
          </template>
        </fdx-dropdown>

        <slot></slot>
      </div>
    </div>
  </header>
</template>
